/**
 * Statement Button Component
 * Generates and opens PDF statement in new window
 */

import { useState } from 'react';
import { AxiosError } from 'axios';
import { Button, ErrorMessage } from '@/components/common';
import { generateStatement } from '@/api/services/statementService';

export default function StatementButton() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleViewStatement = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get PDF blob from API (policy number comes from JWT token)
      const pdfBlob = await generateStatement();

      // Create object URL from blob
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open in new window/tab
      const newWindow = window.open(pdfUrl, '_blank');

      if (!newWindow) {
        setError('Pop-up blocked. Please allow pop-ups for this site to view your statement.');
        // Clean up URL if window failed to open
        URL.revokeObjectURL(pdfUrl);
      } else {
        // Clean up URL after a delay to ensure PDF loads
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 100);
      }
    } catch (err) {
      console.error('Failed to load statement:', err);

      if (err instanceof AxiosError) {
        if (err.response?.status === 404) {
          setError('Statement not found. Please contact support if this issue persists.');
        } else if (err.response?.status === 500) {
          setError('Server error while generating statement. Please try again later.');
        } else {
          setError('Failed to load statement. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleViewStatement}
        loading={isLoading}
      >
        View Statement
      </Button>

      {error && <ErrorMessage message={error} variant="inline" />}
    </div>
  );
}
